import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { useDocument } from '../../hooks/useDocuments';
import Spinner from '../Controls/Spinner';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';

/** File extensions that can be shown inline in the browser. */
const VIEWABLE_IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

function getExtension(filename: string): string {
    return (filename || '').split('.').pop()?.toLowerCase() ?? '';
}

function DocumentViewer({ match }: RouteComponentProps<{ documentId: string }>) {
    const documentId = parseInt(match.params.documentId, 10);
    const { data: document, isLoading } = useDocument(documentId);

    if (isLoading) return <Spinner />;

    if (!document) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h6" color="error">
                    Dokumentet hittades inte.
                </Typography>
            </Box>
        );
    }

    const ext = getExtension(document.filename);
    const isPdf = ext === 'pdf';
    const isImage = VIEWABLE_IMAGE_EXTS.includes(ext);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                {document.title}
            </Typography>

            {isPdf && (
                <Box
                    data-testid="pdf-viewer"
                    component="embed"
                    src={document.documentUrl}
                    type="application/pdf"
                    sx={{ width: '100%', height: '80vh', border: '1px solid', borderColor: 'divider' }}
                />
            )}

            {isImage && (
                <Box
                    data-testid="image-viewer"
                    component="img"
                    src={document.documentUrl}
                    alt={document.title}
                    sx={{ maxWidth: '100%', display: 'block' }}
                />
            )}

            {!isPdf && !isImage && (
                <Box sx={{ p: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 1, bgcolor: 'warning.light' }}>
                    <Typography variant="body1" gutterBottom>
                        Det här dokumentformatet kan inte visas i webbläsaren.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        href={document.documentUrl}
                        target="_blank"
                        download
                    >
                        Ladda ner
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default DocumentViewer;
